/* Section titles colour their stressed phrase with the accent colour. In
   English and Malay that phrase is simply the last whitespace-separated word,
   so it can be inferred. A Chinese title has no whitespace to split on, so it
   marks the phrase itself with **double asterisks**, which is the only way the
   accent can land on the intended characters rather than nowhere at all.

   Rules:
   - A `**...**` marker wins over the last-word rule. Text before it is the
     lead, text after it is the tail, so the accent can sit mid-sentence.
   - With no marker, the last whitespace-separated word becomes the accent. A
     string with no whitespace renders whole and unaccented.
   - Markers are stripped from everything returned, including lines that are
     not accented, so one can never reach the page as literal asterisks. */

const ACCENT_MARKER = /\*\*(.+?)\*\*/g;

export function stripAccent(text) {
  return String(text == null ? '' : text).replace(ACCENT_MARKER, '$1');
}

export function accentTitle(text) {
  const full = String(text == null ? '' : text).trim();
  const marked = /\*\*(.+?)\*\*/.exec(full);

  if (marked) {
    return {
      lead: full.slice(0, marked.index),
      accent: marked[1],
      tail: full.slice(marked.index + marked[0].length)
    };
  }

  const parts = full.split(/\s+/);

  if (parts.length < 2) {
    return { lead: full, accent: '', tail: '' };
  }

  /* Trailing punctuation stays outside the accent, so a sentence ending in a
     full stop does not render a blue period. */
  const last = parts[parts.length - 1];
  const peeled = /^(.*?)([.,!?;:\u2026\u3002\uff01\uff1f\uff1b\uff1a]+)$/.exec(last);
  const word = peeled ? peeled[1] : last;
  const punct = peeled ? peeled[2] : '';

  if (!word) {
    return { lead: full, accent: '', tail: '' };
  }

  return {
    lead: parts.slice(0, -1).join(' ') + ' ',
    accent: word,
    tail: punct
  };
}

/* Multi-line variant for titles that carry `\n` to force a line break. Only the
   last line takes the accent, which keeps a single-line title rendering as it
   always has. */
export function accentTitleLines(text) {
  const lines = String(text == null ? '' : text)
    .split('\n')
    .map((line) => line.trim())
    .filter((line) => line.length > 0);

  if (!lines.length) {
    return [];
  }

  return lines.map((line, index) => {
    if (index === lines.length - 1) {
      return accentTitle(line);
    }

    return { lead: stripAccent(line), accent: '', tail: '' };
  });
}
